// Copyright (c) 2025 ParkEngage Inc. All rights reserved.

// This software is the confidential and proprietary information of
// ParkEngage Inc. ("Confidential Information"). You shall not
// disclose such Confidential Information and shall use it only in
// accordance with the terms of the license agreement you entered into
// with ParkEngage Inc.

// This source code is a valuable trade secret of ParkEngage Inc.

// THIS SOFTWARE IS PROVIDED BY ParkEngage Inc. ``AS IS'' AND ANY
// EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR
// PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL [YOUR COMPANY NAME] OR
// CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
// EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
// PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
// PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY
// OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
// OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.

// The views and conclusions contained in the software and documentation
// are those of the authors and should not be interpreted as representing
// official policies, either expressed or implied, of ParkEngage Inc.

import React from 'react'
import PropTypes from 'prop-types'
import { Circles } from 'react-loader-spinner'

const TextInput = (props) => {

    const {
        noParentMargin, name, label, onClick, parentId, parentType, parentListOrder, disabled, defaultValue,
        btnIcon, btnSpanName, btnSpanClass, btnText, btnClick, isSubmitting, onChange, onKeyPress, onKeyUp,
        placeholder, description, value, checked, type, readOnly, min, maxLength, id, containerClass,
        required, minLength, validation, autoFocus, className, btnIconClass, max, errorMessage, customStyle,
        pattern, onKeyDown, isCheckBoxHeading, onBlur, onFocus, spaceNotAllowed = false, labelClass
    } = props
    const onInputChange = (event) => {
        if (!onChange) return

        if (event.target.type === 'text') {
            const value = event.target.value
            const sanitizedValue = value.replace(/\s\s+/g, ' ')
            if (value !== sanitizedValue) {
                event.target.value = sanitizedValue
            }
        }

        onChange(event)
    }

    const handleKeyDown = (event) => {
        // Prevent space character
        if (event.key == ' ' && spaceNotAllowed) {
            event.preventDefault()
        }

        // Call parent's onKeyDown if provided
        if (typeof props.onKeyDown == 'function') {
            onKeyDown(event)
        }
    }



    return (
        <div className={containerClass || ''} style={customStyle}>
            {type === 'checkbox' ? (
                <div className='form-group'>
                    <label className='formCont font-bold' htmlFor={name}>
                        <input
                            data-parentid={parentId ?? null}
                            data-type={parentType ?? null}
                            data-listorder={parentListOrder ?? null}
                            disabled={disabled}
                            readOnly={readOnly}
                            name={name}
                            className='filled-in'
                            type={type || 'text'}
                            placeholder={placeholder}
                            value={value}
                            defaultValue={defaultValue}
                            onChange={onChange}
                            onKeyPress={onKeyPress}
                            onKeyUp={onKeyUp}
                            onKeyDown={handleKeyDown}
                            checked={checked}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            min={min}
                            maxLength={maxLength}
                            minLength={minLength}
                            id={id}
                            required={!!required}
                            autoComplete='off'
                        />
                        <span className={`checkmark ${isCheckBoxHeading ? 'font-weight-bold' : ''}`}>{label}</span>
                        <div className='b-input'></div>
                    </label>
                </div>
            ) : (
                <div className={noParentMargin ? 'form-group my-0' : 'form-group'}>
                    {label && (
                        <label htmlFor={name} className={labelClass || ''}>
                            {label} <span style={{ color: 'red' }}>{(validation || required) ? '*' : ''}</span>
                        </label>
                    )}
                    <div className='d-flex align-items-center'>
                        <input
                            disabled={disabled}
                            readOnly={readOnly}
                            autoFocus={autoFocus}
                            name={name}
                            className={className || 'validate form-control'}
                            type={type || 'text'}
                            placeholder={placeholder}
                            value={value}
                            defaultValue={defaultValue}
                            onChange={onInputChange}
                            onClick={onClick}
                            onKeyPress={onKeyPress}
                            onKeyUp={onKeyUp}
                            onKeyDown={handleKeyDown}
                            checked={checked}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            min={min}
                            max={max}
                            maxLength={maxLength}
                            minLength={minLength}
                            id={id}
                            required={!!required}
                            autoComplete='off'
                            pattern={pattern}
                        />
                        {(btnIcon || btnSpanName) && (
                            <button
                                className={btnIconClass || 'btn btn-outline-secondary btn-sm'}
                                onClick={btnClick}
                                disabled={isSubmitting}
                                type='button'
                            >
                                {btnIcon && <i className={btnIcon}></i>}
                                {btnSpanName && <span name={btnSpanName} className={btnSpanClass}></span>}{' '}
                                {btnText || ''}{' '}
                                {isSubmitting && <Circles
                                    height="20"
                                    width="20"
                                    color="#ffffff"
                                    ariaLabel="circles-loading"
                                    visible={true}
                                />}
                            </button>
                        )}
                    </div>
                    {errorMessage && (
                        <div className='error-message'>
                            {typeof errorMessage === 'function' ? errorMessage() : errorMessage}
                        </div>
                    )}
                </div>
            )}
            {description && <p>{description}</p>}
        </div>
    )
}

TextInput.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    defaultValue: PropTypes.string,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onKeyPress: PropTypes.func,
    onKeyUp: PropTypes.func,
    onKeyDown: PropTypes.func,
    onBlur: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    onFocus: PropTypes.func,
    description: PropTypes.string,
    type: PropTypes.string,
    checked: PropTypes.bool,
    readOnly: PropTypes.bool,
    disabled: PropTypes.bool,
    min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.string,
    containerClass: PropTypes.string,
    required: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    validation: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    btnClick: PropTypes.func,
    pattern: PropTypes.string,
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    btnIcon: PropTypes.string,
    btnSpanName: PropTypes.string,
    btnSpanClass: PropTypes.string,
    btnText: PropTypes.string,
    isSubmitting: PropTypes.bool,
    btnIconClass: PropTypes.string,
    customStyle: PropTypes.object,
    noParentMargin: PropTypes.bool,
    parentId: PropTypes.any,
    parentType: PropTypes.any,
    parentListOrder: PropTypes.any,
    isCheckBoxHeading: PropTypes.bool,
    spaceNotAllowed: PropTypes.bool,
    labelClass: PropTypes.string
}

TextInput.defaultProps = {
    readOnly: false,
    value: undefined
}

export default TextInput
